import { store } from '@/app/store';
import { selectUser } from '@/features/user/userApi';
import PageWrapper from '@/shared/components/PageWrapper/PageWrapper';
import { useWindowDimensions } from '@/shared/hooks/useWindowDimension';
import { toCleanString } from '@/shared/utils/collection';
import { UserData } from '@/types/user';
import { Capacitor } from '@capacitor/core';
import { IonCol, IonGrid, IonItem, IonItemGroup, IonLabel, IonRow } from '@ionic/react';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useRouteMatch } from 'react-router';
import styles from './SettingsViewWrapper.module.css';

type MenuItem = {
  name: string;
  value: string;
  route: string;
  hasLine?: boolean;
};

const MenuItemView: React.FC<MenuItem> = ({ name, value, route, hasLine }) => {
  const { isMobileView } = useWindowDimensions();
  const routeMatch = useRouteMatch(route);
  return (
    <IonItem
      lines={hasLine ? 'full' : 'none'}
      routerLink={route}
      className={`${routeMatch?.isExact && !isMobileView ? styles.itemActive : ''}`}
      detail={isMobileView}
    >
      <IonLabel>{name}</IonLabel>
      {isMobileView && <IonLabel class="ion-text-right">{value}</IonLabel>}
    </IonItem>
  );
};

type MenuGroup = {
  group: string;
  items: MenuItem[];
};

const MenuGroupView: React.FC<MenuGroup> = ({ group, items }) => {
  const { isMobileView } = useWindowDimensions();

  return (
    <IonItemGroup className={isMobileView ? styles.groupMobile : styles.groupDesktop}>
      <IonItem lines="none">
        <IonLabel>{group}</IonLabel>
      </IonItem>
      {items.map((item, index) => (
        <MenuItemView {...item} hasLine={index < items.length - 1 && isMobileView} key={item.name} />
      ))}
    </IonItemGroup>
  );
};

const SettingsViewWrapper: React.FC<{
  name?: string;
  title?: string;
  children: React.ReactNode;
}> = ({ name, title, children }) => {
  const { t } = useTranslation();
  const { isMobileView } = useWindowDimensions();
  const routeSettings = useRouteMatch('/settings');

  const userData: UserData[] = selectUser(store.getState());

  const user = userData[0];

  // appears in desktop view across all route settings and mobile view with exact route settings
  const showSettingsList = (routeSettings?.isExact && isMobileView) || !isMobileView;

  // appears in desktop view across all route settings and mobile view with child route settings
  const showSettingsPage = (!routeSettings?.isExact && isMobileView) || !isMobileView;

  const isMainPage = (routeSettings?.isExact && isMobileView) || !isMobileView;

  const pageName = name || 'settings-page';

  const fullName = toCleanString([user?.firstName, user?.lastName]);

  const address = toCleanString([
    user?.address?.line1,
    user?.address?.street,
    user?.address?.houseNumber,
    user?.address?.city,
    user?.address?.postalCode,
    user?.address?.country
  ]);

  const menus = [
    {
      group: t('general.profile'),
      items: [
        {
          name: t('settings.personal'),
          value: fullName,
          route: '/settings/personal'
        },
        {
          name: t('settings.company'),
          value: address,
          route: '/settings/company'
        }
      ]
    },
    {
      group: t('settings.login_and_security'),
      items: [
        {
          name: t('data.password'),
          value: '',
          route: '/settings/password'
        },
        {
          name: t('settings.log_in_and_security.user_sessions.device'),
          value: '', // todo
          route: '/settings/devices'
        },
        Capacitor.isNativePlatform()
          ? {
              name: t('general.pin'),
              value: '', // todo
              route: '/settings/pin'
            }
          : undefined
      ].filter((item) => !!item) as MenuItem[]
    },
    {
      group: t('general.forms'),
      items: [
        {
          name: t('settings.forms.user_change'),
          value: '',
          route: '/settings/user-change'
        },
        {
          name: t('settings.forms.address_change'),
          value: '',
          route: '/settings/address-change'
        }
      ]
    },
    {
      group: t('settings.account_and_taxes'),
      items: [
        {
          name: t('settings.custody_reports'),
          value: '',
          route: '/settings/custody-reports'
        }
      ]
    },
    {
      group: t('settings.other'),
      items: [
        {
          name: t('general.help.info'),
          value: '',
          route: '/settings/help'
        }
      ]
    }
  ];

  return (
    <PageWrapper name={pageName} mainPage={isMainPage}>
      <IonGrid className={!isMobileView ? styles.pageWrapper : ''}>
        <IonRow>
          {showSettingsList && (
            <IonCol sizeLg="3" sizeMd="12">
              {menus.map((menu) => (
                <MenuGroupView {...menu} key={menu.group} />
              ))}
            </IonCol>
          )}
          {showSettingsPage && (
            <IonCol sizeLg="9" sizeMd="12">
              {title && <h1 className={styles.title}>{title}</h1>}
              {children}
            </IonCol>
          )}
        </IonRow>
      </IonGrid>
    </PageWrapper>
  );
};

export default SettingsViewWrapper;
